import NiceModal, {NiceModalHocProps} from "@ebay/nice-modal-react";
import {KeyboardArrowLeft, KeyboardArrowRight} from "@mui/icons-material";
import {styled} from "@mui/material/styles";
import {LocalizationProvider, StaticDatePicker, TimeClockProps} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, {Dayjs} from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, {useState} from "react";
import {Colors, Container} from "../../../../ui";
import {Dialog} from "./Dialog";

dayjs.extend(utc);
dayjs.extend(timezone);

const getFutureDate = (date: Dayjs, daysToAdd: number): Dayjs => {
  return date.add(daysToAdd, "day");
};

const TZ = "America/New_York";

interface IChooseDateProps extends NiceModalHocProps {
  title?: string;
  subtitle?: string;
  onConfirm: (event: any) => void;
  shouldDisableDate?: (date: unknown) => boolean;
  startDate: Dayjs;
  isPastAllowed?: boolean;
  isChooseYearAllowed?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

const StyledArrowLeft = styled(KeyboardArrowLeft)<{disabled: boolean}>(({theme}) => ({
  color: Colors.grey._900,
}));

const StyledArrowRight = styled(KeyboardArrowRight)<{disabled: boolean}>(({theme}) => ({
  color: Colors.grey._900,
}));

export const slots: TimeClockProps<any>["slots"] = {
  leftArrowIcon: StyledArrowLeft,
  rightArrowIcon: StyledArrowRight,
};

const ChooseDateDialog = NiceModal.create(
  ({
    title,
    subtitle = "When do you want to make this payment?",
    onConfirm,
    shouldDisableDate,
    startDate,
    isPastAllowed = false,
    isChooseYearAllowed = false,
    minDate,
    maxDate,
  }: IChooseDateProps) => {
    const [selectedDate, setSelectedDate] = useState<Dayjs>(startDate ?? dayjs());
    const confirmHandler = (): void => {
      onConfirm(selectedDate);
    };

    const getMinDate = (): Dayjs | null => {
      if (minDate) {
        return minDate.tz(TZ).startOf("day");
      }
      return isPastAllowed ? null : startDate.tz(TZ).startOf("day");
    };

    const getMaxDate = (): Dayjs | null => {
      if (maxDate) {
        return maxDate.tz(TZ).endOf("day");
      }
      return dayjs(getFutureDate(getMinDate() || dayjs(), 30));
    };

    return (
      <Dialog title={title || "Payment date"} onClick={confirmHandler} buttonText={"Set"}>
        {subtitle}
        <Container
          fullWidth
          sx={{
            gap: "5px",
            justifyContent: "space-around",
            overflow: "auto",
            border: `1px solid ${Colors.grey._500}`,
            borderRadius: "8px",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDatePicker
              defaultValue={dayjs()}
              value={selectedDate || null}
              minDate={getMinDate()}
              maxDate={getMaxDate()}
              onChange={(event: any) => {
                setSelectedDate(event);
              }}
              shouldDisableDate={shouldDisableDate}
              views={isChooseYearAllowed ? ["day", "month", "year"] : ["day"]}
              slots={slots}
              slotProps={{
                actionBar: {
                  actions: [],
                },
                toolbar: {hidden: true},
              }}
            />
          </LocalizationProvider>
        </Container>
      </Dialog>
    );
  }
);

export {ChooseDateDialog, getFutureDate};
